import * as React from "react";

import {
  ReactCheckout2022Route,
  useDrawerDelivery_asController,
  useMyBagPresentationModel,
  useMyBagUserExperience,
  useMyBagUserExperience_asController,
  useReactCheckout2022Router,
  useDeliveryDetailsUserExperience_asController,
} from "../providers";

import { FragmentSummaryByTheNumbers } from "../fragment-summary-by-the-numbers";

import { MyBagSummary_ActionsExpressCheckout } from "./express-checkout";

export interface MyBagSummaryProps {
  hideExpressCheckout?: boolean;
  hideActions?: boolean;
  hideGiftcardNotice?: boolean;
}
export const MyBagSummary: React.FunctionComponent<MyBagSummaryProps> = (
  props
) => {
  const { currentRoute } = useReactCheckout2022Router();

  const { presentationMyBagItems, presentationMyBagOrder } =
    useMyBagPresentationModel();

  const { canIProceedToCheckout } = useMyBagUserExperience();
  const { handleCheckout } = useMyBagUserExperience_asController();

  const {
    handleShowDrawerDelivery_viewItemDetailsLink:
      deliveryDetailsUserExperience_handleShowDrawerDelivery_viewItemDetailsLink,
  } = useDeliveryDetailsUserExperience_asController();

  const { handleOpen: drawerDelivery_handleOpen } =
    useDrawerDelivery_asController();

  const [shouldShowMobileFloatingActions, setShouldShowMobileFloatingActions] =
    React.useState(false);

  const actionsRef = React.useRef<HTMLDivElement | null>(null);

  const showOrderSummaryWithItemPictures = React.useMemo(() => {
    switch (currentRoute) {
      case ReactCheckout2022Route.delivery_details:
        return true;
      case ReactCheckout2022Route.payment:
        return true;
      default:
        return false;
    }
  }, [currentRoute]);

  const handleShowDrawerDelivery_viewItemDetailsLink = React.useCallback(
    (event) => {
      event.preventDefault();
      switch (currentRoute) {
        case ReactCheckout2022Route.mybag:
          break;
        case ReactCheckout2022Route.delivery_details:
          deliveryDetailsUserExperience_handleShowDrawerDelivery_viewItemDetailsLink();
          break;
        case ReactCheckout2022Route.payment:
          drawerDelivery_handleOpen();
          break;
        default:
          break;
      }
    },
    [
      currentRoute,
      deliveryDetailsUserExperience_handleShowDrawerDelivery_viewItemDetailsLink,
      drawerDelivery_handleOpen,
    ]
  );

  const orderSummaryWithItemPictures = React.useMemo(() => {
    if (!showOrderSummaryWithItemPictures) {
      return null;
    }

    let items = presentationMyBagItems;
    let howManyMoreItemsAreThere = 0;
    if (presentationMyBagItems.length > 6) {
      items = presentationMyBagItems.slice(0, 5);
      howManyMoreItemsAreThere = presentationMyBagItems.length - 5;
    }
    return (
      <React.Fragment>
        <div className="orderSummaryWithItemPictures">
          <h4>{`Order Summary`}</h4>

          <ul className={`hasThisManyItems--${items.length}`}>
            {items.map((presentationMyBagItem) => (
              <li
                key={presentationMyBagItem.id}
                onClick={handleShowDrawerDelivery_viewItemDetailsLink}
              >
                <img
                  src={presentationMyBagItem.imageThumbnail}
                  alt={presentationMyBagItem.title}
                />
                <p>{`${presentationMyBagItem.distinguishingFeature}`}</p>
                <div className="quantity">
                  <span>{`${presentationMyBagItem.howManyOfThisProduct}`}</span>
                </div>
              </li>
            ))}
            {howManyMoreItemsAreThere > 0 && (
              <li
                className="show-more-tile"
                onClick={handleShowDrawerDelivery_viewItemDetailsLink}
              >
                <p>{`${howManyMoreItemsAreThere} more items`}</p>
              </li>
            )}
          </ul>

          <a
            href="javascript:void(0)"
            onClick={handleShowDrawerDelivery_viewItemDetailsLink}
          >
            <span>{`View item details`}</span>
          </a>
        </div>
      </React.Fragment>
    );
  }, [
    showOrderSummaryWithItemPictures,
    handleShowDrawerDelivery_viewItemDetailsLink,
    presentationMyBagItems,
  ]);

  const actions = React.useMemo(() => {
    if (props.hideActions) {
      return null;
    }
    return (
      <React.Fragment>
        <div ref={actionsRef} className="actions">
          <div
            className={`actions-inner-stickyable-container ${
              shouldShowMobileFloatingActions
                ? "shouldShowMobileFloatingActions"
                : ""
            }`}
          >
            <button
              onClick={handleCheckout}
              disabled={!canIProceedToCheckout}
              className={`${
                !canIProceedToCheckout
                  ? "should-be-disabled"
                  : "should-be-enabled"
              }`}
            >
              <span className="checkout-label">{`CHECKOUT`}&nbsp;</span>
              <span className="dollar-sign">{`\$`}</span>
              <span className="totalValueBreakdown">
                {`${presentationMyBagOrder?.totalValueBreakdown.value.toFixed(
                  2
                )}`}
              </span>
            </button>

            {!props.hideExpressCheckout && (
              <MyBagSummary_ActionsExpressCheckout />
            )}
          </div>
        </div>
      </React.Fragment>
    );
  }, [
    presentationMyBagOrder,
    shouldShowMobileFloatingActions,
    props.hideExpressCheckout,
    props.hideActions,
  ]);

  const giftcardNotice = React.useMemo(() => {
    if (
      props.hideGiftcardNotice ||
      presentationMyBagOrder?.areThereAnyGiftcardsInTheOrder ||
      !!presentationMyBagOrder?.giftCardValueBreakdown
    ) {
      return null;
    }
    return (
      <React.Fragment>
        <div className="notice you-can-apply-a-giftcard">
          <span className="have-a-gift-card">{`Have a gift card`}</span>
          <span className="just-a-question-mark">{`?`}</span>
          <span className="rest">{` Apply it at the last step of checkout.`}</span>
        </div>
      </React.Fragment>
    );
  }, [props.hideGiftcardNotice, presentationMyBagOrder]);

  const handleScroll = React.useCallback(() => {
    let givenThisNewScroll_shouldShowMobileFloatingActions = false;

    const actionsBoundingClientRect =
      actionsRef.current?.getBoundingClientRect();
    if (
      actionsBoundingClientRect &&
      actionsBoundingClientRect.y >= 0 &&
      window.innerHeight - actionsBoundingClientRect.y >= 0
    ) {
      givenThisNewScroll_shouldShowMobileFloatingActions = false;
    } else {
      givenThisNewScroll_shouldShowMobileFloatingActions = true;
    }

    setShouldShowMobileFloatingActions(
      givenThisNewScroll_shouldShowMobileFloatingActions
    );
  }, [actionsRef]);
  React.useEffect(() => {
    if (!presentationMyBagOrder) {
      return;
    }
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [presentationMyBagOrder, handleScroll]);

  if (!presentationMyBagOrder) {
    return null;
  }

  return (
    <section className="my-bag-summary">
      {orderSummaryWithItemPictures}

      <FragmentSummaryByTheNumbers />

      {actions}

      {giftcardNotice}
    </section>
  );
};
